import Typography from '@mui/material/Typography';
import { Translation } from '@ps/web-shared-components';
import React, { useEffect, useState } from 'react';
import { useField } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';

import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';
import type { Dispatch } from '@swf/core/store';

import type { PaySystemDetails } from '../../../../../../../types/Cashier';
import { convertAmountByRate, getConversionRate } from '../../../../../../../utils/cashier';
import { calculateFee } from '../../../../../../../utils/fee';
import Checkbox from '../../../../../../Checkbox/Checkbox';
import ConversionCurrency from '../../../../../../ConversionCurrency/ConversionCurrency';
import Prompt from '../../../../../../Prompt/Prompt';
import type { Props } from '../DepositFeePrompts';

import {
  TXTWCL_Amount_receivedX_,
  TXTWCL_Continue,
  TXTWCL_DonXt_show_this_again,
  TXTWCL_N0NN_Commission,
  TXTWCL_Please_noteX_There_is_a_N0NN_c_ELL,
  TXTWCL_Total_depositX_
} from './FeePromptRO.messages.json';
import { checkbox, currencyContainer, currencyConvertor, title } from './FeePromptRO.scss';

const FeePromptRO: React.FC<Props> = ({ onConfirm, onClose }) => {
  const { localSettings } = useDispatch<Dispatch>();
  const isPromptDisabled = useSelector(select.localSettings.getCashier)?.disableFeePrompt;
  const [checked, setChecked] = useState(false);
  const {
    data: { paySystemDetails }
  } = useWizardContext();

  const {
    input: {
      value: { amount }
    }
  } = useField('deposit', { subscription: { value: true } });
  const {
    input: { value: currency }
  } = useField('deposit.currency', { subscription: { value: true } });

  const {
    paysysFee: paySystemFee,
    rates: { curr: balanceCurrency, rates }
  }: PaySystemDetails = paySystemDetails;

  const commission = paySystemFee.rate_x_100K / 1000;
  const conversionRate = getConversionRate(rates, currency);
  const convertedAmount = convertAmountByRate(amount || 0, conversionRate, balanceCurrency, false);
  const fee = calculateFee(convertedAmount, paySystemFee);
  const netAmount = convertedAmount - fee;

  const onConfirmHandler = () => {
    if (checked) {
      localSettings.set({
        cashier: {
          disableFeePrompt: true
        }
      });
    }
    onConfirm();
  };

  useEffect(() => {
    if (isPromptDisabled) {
      onConfirm();
    }
  }, [isPromptDisabled, onConfirm]);

  if (isPromptDisabled) {
    return null;
  }

  return (
    <Prompt
      variant="default"
      title={{
        translation: TXTWCL_N0NN_Commission,
        values: { 0: commission }
      }}
      primaryButton={{ translation: TXTWCL_Continue }}
      onPrimaryButtonClick={onConfirmHandler}
      onClose={onClose}
    >
      <Typography component="div">
        <p className={title}>
          <Translation {...TXTWCL_Please_noteX_There_is_a_N0NN_c_ELL} values={{ 0: commission }} />
        </p>
        <div className={currencyContainer}>
          <Translation {...TXTWCL_Total_depositX_} />
          <ConversionCurrency
            className={currencyConvertor}
            amount={convertedAmount}
            balanceCurrency={balanceCurrency}
          />
        </div>
        <div className={currencyContainer}>
          <Translation {...TXTWCL_Amount_receivedX_} />
          <ConversionCurrency
            className={currencyConvertor}
            amount={netAmount}
            balanceCurrency={balanceCurrency}
          />
        </div>
        <div className={checkbox}>
          <Checkbox
            checked={checked}
            onChange={() => setChecked(!checked)}
            label={TXTWCL_DonXt_show_this_again}
          />
        </div>
      </Typography>
    </Prompt>
  );
};

export default FeePromptRO;
